/**
 * 🎯 Universal Widget Utilities
 * 
 * Gemeinsame Funktionen für alle Widgets.
 * Reduziert Code-Duplikation und erhöht Konsistenz.
 */
import { useState } from 'react';
import DeleteModal from '../WidgetTypes/DeleteModal';

// Utility Functions
export function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

export function appendPercent(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    const percentValue = Math.min(Number(value), 100);
    return `${percentValue}%`;
}

export function isDark(color) {
    const rgb = parseInt(color.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >>  8) & 0xff;
    const b = (rgb >>  0) & 0xff;
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    return luma < 128;
}

// Advanced color brightness detection for automatic icon inversion
export function isLightColor(color) {
    if (!color || typeof color !== 'string') return false;
    
    // Handle different color formats
    let r, g, b;
    
    if (color.startsWith('#')) {
        // Hex color
        const hex = color.slice(1);
        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        }
    } else if (color.startsWith('rgb')) {
        // RGB/RGBA color
        const matches = color.match(/\d+/g);
        if (matches && matches.length >= 3) {
            r = parseInt(matches[0]);
            g = parseInt(matches[1]);
            b = parseInt(matches[2]);
        } else {
            return false;
        }
    } else {
        // Named colors or other formats - conservative approach
        return false;
    }
    
    // Calculate relative luminance (more accurate than simple luma)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if color is light (threshold 0.7 for automatic inversion)
    return luminance > 0.7;
}

// Smart icon inversion logic
export function shouldInvertIcons(titleColor, manualOverride = null) {
    // If manual override is set, use that
    if (manualOverride !== null) {
        return manualOverride;
    }
    
    // Otherwise, auto-detect based on title color brightness
    return isLightColor(titleColor);
}

// Widget Border Styling
export function getBorderStyle(isEditMode, isSelected, data) {
    if (isEditMode) {
        if (isSelected) {
            const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
            return `2px dashed ${borderColor}`;
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
        }
    } else {
        return data.border ? `1.5px solid ${data.borderColor}` : 'none';
    }
}

// Standard Widget Style Generator
export function getWidgetStyle(data, isEditMode, isSelected) {
    return {
        backgroundColor: data.backgroundColor,
        marginBottom: appendPx(data.marginBottom),
        minHeight: appendPx(data.height),
        marginTop: appendPx(data.marginTop),
        border: getBorderStyle(isEditMode, isSelected, data),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none'
    };
}

// Delete Modal Logic
export function useDeleteModal(onRemove, data) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    const deleteButton = (
        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
            <img className="delete_logo" src="/img/delete.svg" alt="Löschen" />
        </button>
    );

    const deleteModal = (
        <DeleteModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDelete}
        />
    );

    return {
        showDeleteModal,
        setShowDeleteModal,
        handleDelete,
        deleteButton,
        deleteModal
    };
}

// Image Upload Logic
export function useImageUpload(data, onUpdate) {
    const handleImageUpload = (fieldName, setUploadingState) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file && onUpdate) {
                setUploadingState(true);
                
                // Simuliere Upload (in echter App: Firebase Upload)
                setTimeout(() => {
                    const fakeUrl = URL.createObjectURL(file);
                    const updatedData = { ...data, [fieldName]: fakeUrl };
                    onUpdate(updatedData);
                    setUploadingState(false);
                }, 2000);
            }
        };
        input.click();
    };

    return { handleImageUpload };
}

// CSS Animations für alle Widgets
export const CSS_ANIMATIONS = `
    @keyframes blink {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0; }
    }
    
    @keyframes silverGlow {
        0% { 
            background: linear-gradient(45deg, 
                rgba(255, 255, 255, 0.02) 0%,
                rgba(192, 192, 192, 0.03) 25%,
                rgba(255, 255, 255, 0.04) 50%,
                rgba(192, 192, 192, 0.03) 75%,
                rgba(255, 255, 255, 0.02) 100%
            );
        }
        50% { 
            background: linear-gradient(45deg, 
                rgba(255, 255, 255, 0.04) 0%,
                rgba(192, 192, 192, 0.05) 25%,
                rgba(255, 255, 255, 0.06) 50%,
                rgba(192, 192, 192, 0.05) 75%,
                rgba(255, 255, 255, 0.04) 100%
            );
        }
        100% { 
            background: linear-gradient(45deg, 
                rgba(255, 255, 255, 0.02) 0%,
                rgba(192, 192, 192, 0.03) 25%,
                rgba(255, 255, 255, 0.04) 50%,
                rgba(192, 192, 192, 0.03) 75%,
                rgba(255, 255, 255, 0.02) 100%
            );
        }
    }
    
    @keyframes silverShimmer {
        0% { background-position: -200% center; }
        100% { background-position: 200% center; }
    }
    
    @keyframes heroImageUploadSpin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;
